import { Component, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { AdminService } from "../../admin.service";
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from 'src/app/users/users.service';
import { DeviceService } from 'src/app/home/site-dashboard/device/device.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DomainsService } from 'src/app/shared/services/domains.service';
import { DevicesFamily } from 'src/app/device/device-management.service';

@Component({
	selector: 'app-connectivity-verification-log',
	templateUrl: './connectivity-verification-log.component.html',
	styleUrls: ['./connectivity-verification-log.component.css']
})

export class ConnectivityVerificationLogComponent implements OnInit {
	// custom table test
	tableData: TableData[] = [];

	tableConfig: TableConfig = {
		hasExport: true,
		fileName: `${this.translate.instant('connectivity_verification.file_name')} ${moment.unix(this.commonService.nowTime()).utc().format('MM/DD/YYYY')}`,
		hasPagination: true,
		pageSize: 100,
		draggable: true,
		hasActionButtons: true,
		csvFileHeaderInfo: [this.translate.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')]
	};

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		action_time: TimeSource.PcTime,
		device_last_connect_time_verified_at: TimeSource.PcTime,
		// This page's own formateDate() calls .utc(), unlike the two above.
		last_connect_time: TimeSource.Utc,
	};

	columnConfig: ColumnsConfig[] = [];

	connectivityDetails: any[] = [];
	warnings: any = [];
	routeSubscription: any;
	connectivityLog: any[] = [];
	reportId = 0;
	isImDevice = false;
	selectedDeviceFamily = DevicesFamily.TELEMETRICS;
	deviceFamiliesList = [
		{ id: DevicesFamily.TELEMETRICS, name: this.translate.instant('connectivity_verification.iotah_family') },
		{ id: DevicesFamily.IOTEMETER, name: this.translate.instant('connectivity_verification.iotemeter_family') }
	];

	/** IoTEmeter records voltage and current per phase; IoTAh records a single value for each. */
	private readonly phases = ['a', 'b', 'c'];

	private columnsBuiltFor: string;

	get isIotemeterFamily() {
		return this.selectedDeviceFamily == DevicesFamily.IOTEMETER;
	}

	// filters
	isValidPeriod: boolean = true;
	filter = {
		startPeriod: {month: null, year: null},
		endPeriod: {month: null, year: null},
	};

	filtersList = {
		startPeriod: {months: [], years: []},
		endPeriod: {months: [], years: []},
	};

	@ViewChild("connectivitVerificationModel") connectivitVerificationModel;

	constructor(
		private adminService: AdminService,
		private usersService: UsersService,
		private router: Router,
		private deviceService: DeviceService,
		public translate: TranslateService,
		public commonService: CommonService,
		private route: ActivatedRoute,
		private domainsService: DomainsService,
	) {}

	ngOnInit() {
		if (!this.usersService.hasAccessPermission(null, 'noc'))
				this.router.navigate(['/unauthorized']);

		if (this.domainsService.isDevelopmentDomain)
			this.tableConfig.pageSize = 5;

		this.routeSubscription = this.route.params.subscribe(params => {
			this.reportId = +params.reportId;
			this.isImDevice = params.isImDevice === 'im';

			if (this.isImDevice)
				this.selectedDeviceFamily = DevicesFamily.IOTEMETER;

			if (this.reportId)
				this.getConnectivityVerificationLogs();
			else {
				this.prepareFilters();
				this.prepareColumnConfig();
			}
		});
	}

	prepareColumnConfig() {
		const columnConfig: ColumnsConfig[] = [
			{ key: 'id', name: 'id', type: 'id'},
			{ key: 'current_customer_name', name: this.translate.instant('connectivity_verification.current_customer_name'), type: "link"},
			{ key: 'current_site_name', name: this.translate.instant('connectivity_verification.current_site_name'), type: 'link'},
			{ key: 'customer_name', name: this.translate.instant('connectivity_verification.customer_name'), type: "link", hidden: true},
			{ key: 'site_name', name: this.translate.instant('connectivity_verification.site_name'), type: "link", hidden: true},
			{ key: 'serial_number', name: this.translate.instant('g.serial_number'), type: "link"},
			{ key: 'mac_address', name: this.translate.instant('g.mac_address'), type: "link"},
			{ key: 'user', name: this.translate.instant('g.done_by'), type: "link"},
			{ key: 'action_time', name: this.translate.instant('connectivity_verification.check_time'), type: "date_with_ref"},
			{ key: 'result', name: this.translate.instant('g.result'), type: "boolean",
					filterTrueText: this.translate.instant('connectivity_verification.pass'),
					filterFalseText: this.translate.instant('connectivity_verification.fail'),
			}
		];

		// IoTEmeter checks never record test mode, so the column is not offered for that family.
		if (!this.isIotemeterFamily)
			columnConfig.push({ key: 'test_mode', name: this.translate.instant('connectivity_verification.test_mode'), type: "boolean",
					filterTrueText: this.translate.instant('g.yes'),
					filterFalseText: this.translate.instant('g.no'),
					noValueString: '--'
			});

		columnConfig.push(
			{ key: 'last_connect_time', name: this.translate.instant('devices.estimated_last_connect_date'), type: 'date', hidden: true},
			{ key: 'device_last_connect_time_verified_at', name: this.translate.instant('connectivity_verification.device_last_connect_time_verified_at'), type: 'date_with_ref', hidden: true},
			{ key: 'connectivity_status', name: this.translate.instant('devices.connectivity_status'), type: 'boolean', hidden: true,
					filterTrueText: this.translate.instant('g.connected'),
					filterFalseText: this.translate.instant('g.disconnected')
			},
			{ key: 'longitude', name: this.translate.instant('site_edit.longitude'), type: 'number', hidden: true },
			{ key: 'latitude', name: this.translate.instant('site_edit.latitude'), type: 'number', hidden: true }
		);

		if (this.isIotemeterFamily) {
			for (const phase of this.phases) {
				columnConfig.push({
					key: `current_${phase}`,
					name: this.translate.instant(`connectivity_verification.current_phase_${phase}`),
					type: 'number', hidden: true, noValueString: '---'
				});
				columnConfig.push({
					key: `voltage_${phase}`,
					name: this.translate.instant(`connectivity_verification.voltage_phase_${phase}`),
					type: 'number', hidden: true, noValueString: '---'
				});
			}

			// Shown by default: the one IoTEmeter-specific reading that fits without crowding the
			// table, so switching family actually brings an IoTEmeter field into view. The six
			// per-phase columns stay hidden - showing them would take the table to 14 columns.
			columnConfig.push({
				key: 'voltage_reference',
				name: this.translate.instant('connectivity_verification.voltage_reference'),
				type: 'number', noValueString: '---'
			});
		} else {
			columnConfig.push(
				{ key: 'current', name: this.translate.instant('qv.current'), type: 'number', hidden: true },
				{ key: 'voltage', name: this.translate.instant('qv.voltage'), type: 'number', hidden: true },
				{ key: 'app_version', name: this.translate.instant('connectivity_verification.app_version'), type: 'number', hidden: true }
			);
		}

		columnConfig.push({ key: 'warning_ids', name: this.translate.instant('g.warnings'), type: 'list', hidden: true});

		this.commonService.applyTimeSourceTooltips(columnConfig, this.timeSourceByColumn);
		this.columnConfig = columnConfig;
		this.columnsBuiltFor = this.selectedDeviceFamily;
	}

	/** IoTEmeter stores voltage and current as a three-phase object; anything else has no phases. */
	getPhaseValue(value: any, phase: string) {
		return value && typeof value === 'object' ? value[phase] ?? null : null;
	}

	getConnectivityVerificationLogs() {
		const fromMonth = this.reportId ? '' : `${this.filter.startPeriod.year}${(String(this.filter.startPeriod.month).padStart(2, '0'))}`;
		const toMonth = this.reportId ? '' : `${this.filter.endPeriod.year}${String(this.filter.endPeriod.month).padStart(2, '0')}`;
		const requestedFamily = this.selectedDeviceFamily;

		this.adminService.getConnectivityVerificationLogs(
			this.reportId,
			fromMonth,
			toMonth,
			this.reportId ? this.isImDevice : undefined,
			this.reportId ? undefined : this.selectedDeviceFamily
		).subscribe((res: any) => {

			if (!this.reportId && requestedFamily !== this.selectedDeviceFamily)
				return;

			this.connectivityLog = res;

			if (this.columnsBuiltFor !== this.selectedDeviceFamily)
				this.prepareColumnConfig();

			const tableData: TableData[] = [];
			[...this.connectivityLog].forEach((log: any) => {
				let rewData: TableData = {
					id: {value: log.id},
					current_customer_name: {value: log.current_customer_name, link: ['/', log.device_customer_id]},
					current_site_name: {value: log.current_site_name, link: ['/', log.device_customer_id, log.device_site_id]},
					customer_name: {
						value: log.customer_name == 'customer_deleted'? this.translate.instant("connectivity_verification.customer_deleted") : log.customer_name,
						link: log.customer_name == 'customer_deleted'? null : ['/', log.customer_id]
					},
					site_name: {
						value: log.site_name == 'site_deleted'? this.translate.instant("connectivity_verification.site_deleted") : log.site_name,
						link: log.site_name == 'site_deleted'? null : ['/', log.customer_id, log.site_id]
					},
					serial_number: {
						value: log.serial_number == 'device_deleted'? this.translate.instant("connectivity_verification.device_deleted") : log.serial_number,
						link: log.serial_number == 'device_deleted'? null : ['/', log.device_customer_id, log.device_site_id, log.mac_address, 'performance']
					},
					mac_address: {
						value: log.mac_address,
						link: log.serial_number == 'device_deleted'? null : ['/', log.device_customer_id, log.device_site_id, log.mac_address, 'performance']
					},
					user: {value: log.user ? log.user : '----', link: log.user && log.user_id ? ['/user', 'edit', log.user_id] : null},
					action_time: {value: moment(log.action_time * 1000).format('MM/DD/YYYY hh:mm:ss a')},
					result: {
						value: this.checkProcessStatus(log),
						trueText: this.translate.instant('connectivity_verification.pass'),
						falseText: this.translate.instant('connectivity_verification.fail')
					},
					last_connect_time: {value: this.formateDate(log.last_connect_time * 1000, 'MM/DD/YYYY')},
					device_last_connect_time_verified_at: {value: moment(log.device_last_connect_time_verified_at * 1000).format('MM/DD/YYYY hh:mm:ss a') },
					connectivity_status: {
						value: log.connectivity_status,
						trueText: this.translate.instant('g.connected'),
						falseText: this.translate.instant('g.disconnected')
					},
					longitude: {value: log.longitude},
					latitude: {value: log.latitude}
				}

				if (this.isIotemeterFamily) {
					for (const phase of this.phases) {
						rewData[`current_${phase}`] = {value: this.getPhaseValue(log.current, phase)};
						rewData[`voltage_${phase}`] = {value: this.getPhaseValue(log.voltage, phase)};
					}

					rewData.voltage_reference = {value: log.voltage_reference};
				} else {
					rewData.test_mode = {
						value: log.test_mode,
						trueText: this.translate.instant('g.yes'),
						falseText: this.translate.instant('g.no')
					};
					rewData.current = {value: log.current};
					rewData.voltage = {value: log.voltage};
					rewData.app_version = {value: log.app_version};
				}

				rewData.warning_ids = {value: [...this.prepareWarnings(this.isIotemeterFamily ? log.warnings : log.warning_ids)]};

				tableData.push(rewData);
			});

			this.tableData = tableData;
		});
	}

	prepareFilters() {
		const lastMonthMoment = moment().subtract(1, 'month');
		const currentYear = moment().year();
		const currentMonth = moment().month() + 1;
		const startYear = 2024;
		const startMonth = 1;

		this.filtersList.startPeriod.years = [];
		for (let year = startYear; year <= currentYear; year++) {
			this.filtersList.startPeriod.years.push({ id: year, name: year });
		}

		this.filtersList.startPeriod.months = [];
		for (let month = startMonth; month <= currentMonth; month++) {
			this.filtersList.startPeriod.months.push({ id: month, name: month });
		}

		this.filtersList.endPeriod.years = [...this.filtersList.startPeriod.years];
		this.filtersList.endPeriod.months = [...this.filtersList.startPeriod.months];

		this.filter = {
			startPeriod: {
				year: lastMonthMoment.year(),
				month: lastMonthMoment.month() + 1
			},
			endPeriod: {
				year: currentYear,
				month: currentMonth
			}
		};
	}

	onSelectPeriod() {
		if (this.notValidPeriod())
			return this.isValidPeriod = false;

		this.isValidPeriod = true;
	}

	updateYearMonths(field: 'startPeriod' | 'endPeriod' = null) {
		let months = 12;
		const currentMonth = moment().month() + 1;

		const currentYear = moment().year();
		if (this.filter[field].year === currentYear)
			months = currentMonth;

		this.filtersList[field].months = [];
		for (let month = 1; month <= months; month++) {
			this.filtersList[field].months.push({ id: month, name: month });
		}
	}

	notValidPeriod() {
		return (this.filter.startPeriod.year > this.filter.endPeriod.year ||
				(this.filter.startPeriod.year === this.filter.endPeriod.year && this.filter.startPeriod.month > this.filter.endPeriod.month));
	}

	prepareWarnings(data: any) {
		if (!Array.isArray(data) || !data.length)
			return [];

		if (this.isIotemeterFamily)
			return data.map((item) => ({ text: item, link: null }));

		return data.map((item) => {
			return {
				text: this.translate.instant(`warnings.${this.deviceService.getWarningsMabValues(item)}`),
				link: null
			}
		});
	}

	checkProcessStatus(record) {
		if (record.proccess_result == null) {
			const warnings = this.isIotemeterFamily ? record.warnings : record.warning_ids;
			return (!warnings || !warnings?.length) && record.connectivity_status;
		}

		return record.proccess_result;
	}

	getWarningsToFilter(warningsIds) {
		return (this.prepareWarnings(warningsIds) || [])
			.map(warning => this.translate.instant(`warnings.${warning}`));
	}

	formateDate(date: moment.MomentInput, format = 'MM/DD/YYYY hh:mm:ss a') {
		return date && date != '---' ? moment(date).utc().format(format || 'MM/DD/YYYY hh:mm:ss a') : '---';
	}
}
